import React from 'react';

import {
  EditOutlined,
  InfoCircleOutlined,
  SettingOutlined,
  ClockCircleOutlined,
  DollarOutlined,
  CheckOutlined,
  CloseOutlined,
} from '@ant-design/icons';
import ListPageHeader from '@shared/ListPageHeader';
import Can from '@shared/permissions/Can';
import { Descriptions, Space, Button, Collapse, Tag, Empty, Table } from 'antd';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { formatDateTime } from '../../../utils/dateTime';

const { Panel } = Collapse;

/**
 * SalesChannelsShow - 通用銷售通路詳情組件
 *
 * @param {Object} props
 * @param {Object} props.salesChannel - 銷售通路資料（由 loader 預載）
 * @param {string} props.role - 角色 (admin 或 operator)
 */
export default function SalesChannelsShow({
  salesChannel,
  role = 'admin',
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const basePath = role === 'operator' ? '/operator' : '/admin';

  const handleEdit = () => {
    navigate(`/sales_channels/${salesChannel.id}/edit`);
  };

  if (!salesChannel) {
    return (
      <div>
        <Empty description={t('common.error.no_data')} />
      </div>
    );
  }

  const breadcrumbItems = role === 'operator'
    ? ['sales_channels', { label: salesChannel.name }]
    : ['data', 'data.sales_channels', { label: salesChannel.name }];

  const headerActions = salesChannel.editable && (
    <Can requiredPermission="sales_channels:write">
      <Button type="primary" icon={<EditOutlined />} onClick={handleEdit}>
        {t('common.button.edit')}
      </Button>
    </Can>
  );

  return (
    <div>
      <ListPageHeader
        basePath={basePath}
        items={breadcrumbItems}
        actions={headerActions}
      />
      <Collapse defaultActiveKey={['basic', 'acceptance', 'order', 'billing']}>
          {/* 基本資訊 */}
          <Panel
            header={
              <Space>
                <InfoCircleOutlined />
                {t(`shared.sales_channels.detail.sections.basic_info`)}
              </Space>
            }
            key="basic"
          >
            <Descriptions column={2} bordered>
              {role === 'operator' && (
                <Descriptions.Item label={t('models.sales_channel.merchant_name')}>
                  {salesChannel.merchant_name || '-'}
                </Descriptions.Item>
              )}
              <Descriptions.Item label={t('models.sales_channel.name')}>
                {salesChannel.name || '-'}
              </Descriptions.Item>
              <Descriptions.Item label={t('models.sales_channel.extra_name')}>
                {salesChannel.extra_name || '-'}
              </Descriptions.Item>
              <Descriptions.Item label={t('models.sales_channel.enabled')}>
                {salesChannel.enabled ? (
                  <Tag color="success">{t('common.enabled')}</Tag>
                ) : (
                  <Tag color="default">{t('common.disabled')}</Tag>
                )}
              </Descriptions.Item>
              <Descriptions.Item label={t('models.sales_channel.map_product_rule')}>
                {salesChannel.map_product_rule
                  ? t(`enums.sales_channel.map_product_rule.${salesChannel.map_product_rule}`)
                  : '-'}
              </Descriptions.Item>
              <Descriptions.Item label={t('models.sales_channel.shipping_mark')}>
                {salesChannel.shipping_mark || '-'}
              </Descriptions.Item>
            </Descriptions>
          </Panel>

          {/* 允收設定（庫存 + 效期） */}
          <Panel
            header={
              <Space>
                <ClockCircleOutlined />
                {t(`shared.sales_channels.detail.sections.acceptance_settings`)}
              </Space>
            }
            key="acceptance"
          >
            {/* 庫存設定 */}
            <h4 style={{ marginBottom: '8px' }}>
              {t('shared.sales_channels.detail.inventory_settings_title')}
            </h4>
            <Descriptions column={2} bordered size="small" style={{ marginBottom: '16px' }}>
              <Descriptions.Item label={t('models.sales_channel.inventory_type')}>
                {salesChannel.inventory_type_name || '-'}
              </Descriptions.Item>
              <Descriptions.Item label={t('models.sales_channel.secondary_inventory_type')}>
                {salesChannel.secondary_inventory_type_name || '-'}
              </Descriptions.Item>
              <Descriptions.Item label={t('models.sales_channel.prioritize_full_box_allocation')}>
                {salesChannel.prioritize_full_box_allocation ? (
                  <CheckOutlined style={{ color: 'green' }} />
                ) : (
                  <CloseOutlined style={{ color: 'lightgrey' }} />
                )}
              </Descriptions.Item>
              <Descriptions.Item label={t('models.sales_channel.same_expiration_date')}>
                {salesChannel.same_expiration_date ? (
                  <CheckOutlined style={{ color: 'green' }} />
                ) : (
                  <CloseOutlined style={{ color: 'lightgrey' }} />
                )}
              </Descriptions.Item>
            </Descriptions>

            {/* 效期管理 */}
            <h4 style={{ marginTop: '16px', marginBottom: '8px' }}>
              {t('shared.sales_channels.detail.expiration_settings_title')}
            </h4>

            {/* 效期機制 */}
            <Descriptions column={1} bordered size="small" style={{ marginBottom: '16px' }}>
              <Descriptions.Item label={t('models.sales_channel.expiration_mechanism')}>
                <Tag color={salesChannel.expiration_mechanism === 'none' ? 'default' : 'blue'}>
                  {t(`enums.sales_channel.expiration_mechanism.${salesChannel.expiration_mechanism}`) || salesChannel.expiration_mechanism}
                </Tag>
              </Descriptions.Item>
            </Descriptions>

            {/* 預設效期設定（無階梯規則時使用） */}
            {salesChannel.expiration_mechanism !== 'none' && (
              <>
                <h4 style={{ marginTop: '16px', marginBottom: '8px' }}>
                  {t('shared.sales_channels.detail.default_expiration_settings')}
                </h4>
                <Descriptions column={2} bordered size="small" style={{ marginBottom: '16px' }}>
                  {salesChannel.expiration_mechanism === 'by_days' ? (
                    <>
                      <Descriptions.Item label={t('models.sales_channel.days_before_expiration')}>
                        {salesChannel.days_before_expiration || 0} {t('common.unit.days')}
                      </Descriptions.Item>
                      <Descriptions.Item label={t('models.sales_channel.oversea_days_before_expiration')}>
                        {salesChannel.oversea_days_before_expiration || 0} {t('common.unit.days')}
                      </Descriptions.Item>
                    </>
                  ) : (
                    <>
                      <Descriptions.Item label={t('models.sales_channel.expiration_ratio')}>
                        {salesChannel.expiration_ratio || 0}%
                      </Descriptions.Item>
                      <Descriptions.Item label={t('models.sales_channel.oversea_expiration_ratio')}>
                        {salesChannel.oversea_expiration_ratio || 0}%
                      </Descriptions.Item>
                    </>
                  )}
                </Descriptions>
              </>
            )}

            {/* 階梯式效期規則 */}
            {salesChannel.sales_channel_rules && salesChannel.sales_channel_rules.length > 0 && salesChannel.expiration_mechanism !== 'none' && (
              <>
                <h4 style={{ marginTop: '16px', marginBottom: '8px' }}>
                  {t('shared.sales_channels.detail.tiered_expiration_rules')}
                </h4>
                <Table
                  dataSource={salesChannel.sales_channel_rules}
                  rowKey="id"
                  pagination={false}
                  size="small"
                  bordered
                  style={{ marginBottom: '16px' }}
                  columns={[
                    {
                      title: t('shared.sales_channels.detail.rule_table.threshold'),
                      dataIndex: 'original_expiration_date_threshold',
                      key: 'threshold',
                      width: '25%',
                      render: (val) => (
                        <span>
                          {t('shared.sales_channels.detail.rule_table.threshold_prefix')} <strong>{val}</strong> {t('common.unit.days')}
                        </span>
                      )
                    },
                    {
                      title: t('shared.sales_channels.detail.rule_table.domestic_products'),
                      key: 'domestic',
                      width: '37.5%',
                      render: (_, record) => {
                        if (salesChannel.expiration_mechanism === 'by_days') {
                          return `${record.days_before_expiration || 0} ${t('common.unit.days')}`;
                        }
                        return `${record.expiration_ratio || 0}%`;

                      }
                    },
                    {
                      title: t('shared.sales_channels.detail.rule_table.overseas_products'),
                      key: 'overseas',
                      width: '37.5%',
                      render: (_, record) => {
                        if (salesChannel.expiration_mechanism === 'by_days') {
                          return `${record.oversea_days_before_expiration || 0} ${t('common.unit.days')}`;
                        }
                        return `${record.oversea_expiration_ratio || 0}%`;

                      }
                    }
                  ]}
                />
              </>
            )}

            {/* 其他效期設定 */}
            <h4 style={{ marginTop: '16px', marginBottom: '8px' }}>
              {t('shared.sales_channels.detail.other_expiration_settings')}
            </h4>
            <Descriptions column={1} bordered size="small">
              <Descriptions.Item label={t('models.sales_channel.enable_last_shipped_expiration_date_check')}>
                {salesChannel.enable_last_shipped_expiration_date_check ? (
                  <CheckOutlined style={{ color: 'green' }} />
                ) : (
                  <CloseOutlined style={{ color: 'lightgrey' }} />
                )}
              </Descriptions.Item>
            </Descriptions>
          </Panel>

          {/* 訂單處理設定 */}
          <Panel
            header={
              <Space>
                <SettingOutlined />
                {t(`shared.sales_channels.detail.sections.order_settings`)}
              </Space>
            }
            key="order"
          >
            <Descriptions column={2} bordered>
              <Descriptions.Item label={t('models.sales_channel.allow_partial_allocation')}>
                {salesChannel.allow_partial_allocation === null ? (
                  <Space>
                    <Tag color="default">{t('shared.sales_channels.detail.use_merchant_default')}</Tag>
                    {salesChannel.merchant_default_allow_partial_allocation ? (
                      <CheckOutlined style={{ color: 'green' }} />
                    ) : (
                      <CloseOutlined style={{ color: 'lightgrey' }} />
                    )}
                  </Space>
                ) : salesChannel.allow_partial_allocation ? (
                  <CheckOutlined style={{ color: 'green' }} />
                ) : (
                  <CloseOutlined style={{ color: 'lightgrey' }} />
                )}
              </Descriptions.Item>
              <Descriptions.Item label={t('models.sales_channel.fulfillment_lot_policy')}>
                {salesChannel.fulfillment_lot_policy
                  ? t(`shared.sales_channels.form.fulfillment_lot_policies.${salesChannel.fulfillment_lot_policy}`)
                  : t('shared.sales_channels.form.placeholders.inherit_merchant')
                }
              </Descriptions.Item>
              <Descriptions.Item label={t('models.sales_channel.auto_complete_fulfillment')}>
                {salesChannel.auto_complete_fulfillment ? (
                  <CheckOutlined style={{ color: 'green' }} />
                ) : (
                  <CloseOutlined style={{ color: 'lightgrey' }} />
                )}
              </Descriptions.Item>
              <Descriptions.Item label={t('models.sales_channel.auto_reallocate_orders')}>
                {salesChannel.auto_reallocate_orders ? (
                  <CheckOutlined style={{ color: 'green' }} />
                ) : (
                  <CloseOutlined style={{ color: 'lightgrey' }} />
                )}
              </Descriptions.Item>
              <Descriptions.Item label={t('models.sales_channel.auto_reallocate_orders_priority')}>
                {salesChannel.auto_reallocate_orders_priority || 0}
              </Descriptions.Item>
            </Descriptions>
          </Panel>

          {/* 計費設定 */}
          <Panel
            header={
              <Space>
                <DollarOutlined />
                {t(`shared.sales_channels.detail.sections.billing_settings`)}
              </Space>
            }
            key="billing"
          >
            <Descriptions column={2} bordered>
              <Descriptions.Item label={t('models.sales_channel.check_fee')}>
                {salesChannel.check_fee || '-'}
              </Descriptions.Item>
              <Descriptions.Item label={t('models.sales_channel.picking_fee_per_item')}>
                {salesChannel.picking_fee_per_item || '-'}
              </Descriptions.Item>
              <Descriptions.Item label={t('models.sales_channel.picking_fee_box')}>
                {salesChannel.picking_fee_box || '-'}
              </Descriptions.Item>
              <Descriptions.Item label={t('models.sales_channel.picking_fee_pallet')}>
                {salesChannel.picking_fee_pallet || '-'}
              </Descriptions.Item>
            </Descriptions>
          </Panel>
        </Collapse>

      {/* 時間戳記 */}
      <Descriptions column={2} bordered style={{ marginTop: '16px' }}>
        <Descriptions.Item label={t('common.created_at')}>
          {salesChannel.created_at ? formatDateTime(salesChannel.created_at) : '-'}
        </Descriptions.Item>
        <Descriptions.Item label={t('common.updated_at')}>
          {salesChannel.updated_at ? formatDateTime(salesChannel.updated_at) : '-'}
        </Descriptions.Item>
      </Descriptions>
    </div>
  );
}
